// packages/web/src/lib/progress-event-emitter.ts
/**
 * diffProgress — diff two CourseProgress snapshots into ProgressEvent[].
 *
 * 19BO+ Line 2 B4.4: the single source of cloud-sync mutations. Called from
 * updateProgress (idb.ts) after every learning write (quiz answer, srs rate,
 * bookmark toggle, diagnose verdict accept, etc.). Each changed field+key
 * becomes one ProgressEvent with a client-minted eventId — the unit of
 * idempotent upload to progress_events (INSERT OR IGNORE on eventId PK).
 *
 * §13.4 field policy:
 *   - record-type cloud fields (wrongAnswers/srs/bookmarks/subjectiveAttempts/
 *     essayDrafts/freeResponseDrafts): per-key diff → one event per changed key.
 *   - quizHistory (array): append-diff → one event per newly-added session.
 *   - diagnosis / diagnosisPending / activeQuizSession (scalar): value-change
 *     diff → one event with itemKey=null (course-level).
 *   - itemOverrides / itemFeedback / repairs: LOCAL-ONLY (§13.4) — never emitted.
 *
 * Pure function — no IDB, no React, no side effects. Trivially testable.
 */

import type { CourseProgress } from '@examforge/shared/schemas';
import type { ProgressEvent, ProgressSyncField } from '@examforge/shared/schemas';

/** Record-type fields diffed per-key. Exported so mergeProgressEvents
 *  (the inbound hydration mirror) classifies fields identically. */
export const RECORD_FIELDS: ProgressSyncField[] = [
  'wrongAnswers',
  'srs',
  'bookmarks',
  'subjectiveAttempts',
  'essayDrafts',
  'freeResponseDrafts',
];

/** Scalar fields diffed as a whole (itemKey=null). Exported for the same reason. */
export const SCALAR_FIELDS: ProgressSyncField[] = [
  'diagnosis',
  'diagnosisPending',
  'activeQuizSession',
];

/** Minimal event shape emitted (caller adds userId before upload). */
export interface EmittedProgressEvent {
  eventId: string;
  field: ProgressSyncField;
  itemKey: string | null;
  payload: Record<string, unknown>;
  clientOccurredAt: number;
}

function mkEventId(): string {
  return `evt_${crypto.randomUUID()}`;
}

/**
 * Diff before → after, returning one ProgressEvent per changed field+key.
 * `clientOccurredAt` is the client clock at write time (ms epoch) — used as the
 * LWW sort key on the server. The caller (updateProgress) passes `Date.now()`.
 */
export function diffProgress(
  courseId: string,
  before: CourseProgress,
  after: CourseProgress,
  clientOccurredAt: number,
): EmittedProgressEvent[] {
  void courseId; // courseId is owned by the caller (updateProgress); not needed for diff
  const events: EmittedProgressEvent[] = [];

  // Record-type fields: per-key diff.
  for (const field of RECORD_FIELDS) {
    const beforeRec = (before[field] ?? {}) as Record<string, unknown>;
    const afterRec = (after[field] ?? {}) as Record<string, unknown>;
    const allKeys = new Set([...Object.keys(beforeRec), ...Object.keys(afterRec)]);
    for (const key of allKeys) {
      const b = beforeRec[key];
      const a = afterRec[key];
      if (JSON.stringify(b) === JSON.stringify(a)) continue; // unchanged
      // A deleted key (a === undefined) is still emitted so the server can
      // record the deletion; the merge layer interprets a null/absent payload.
      events.push({
        eventId: mkEventId(),
        field,
        itemKey: key,
        payload: (a ?? {}) as Record<string, unknown>,
        clientOccurredAt,
      });
    }
  }

  // quizHistory: append-diff (new sessions only). We match by session.id.
  {
    const beforeIds = new Set(before.quizHistory.map((s) => s.id));
    for (const session of after.quizHistory) {
      if (beforeIds.has(session.id)) continue;
      events.push({
        eventId: mkEventId(),
        field: 'quizHistory',
        itemKey: session.id,
        payload: session as unknown as Record<string, unknown>,
        clientOccurredAt,
      });
    }
  }

  // Scalar fields: value-change diff (itemKey=null).
  for (const field of SCALAR_FIELDS) {
    const b = before[field];
    const a = after[field];
    if (JSON.stringify(b) === JSON.stringify(a)) continue;
    events.push({
      eventId: mkEventId(),
      field,
      itemKey: null,
      payload: (a ?? {}) as Record<string, unknown>,
      clientOccurredAt,
    });
  }

  return events;
}

/** Convenience: map EmittedProgressEvent[] → ProgressEvent[] for upload (adds userId). */
export function toUploadEvents(
  emitted: EmittedProgressEvent[],
  userId: string,
  courseId: string,
): ProgressEvent[] {
  return emitted.map((e) => ({
    eventId: e.eventId,
    userId,
    courseId,
    field: e.field,
    itemKey: e.itemKey,
    payload: e.payload,
    clientOccurredAt: e.clientOccurredAt,
  }));
}
